import React, { useState, useEffect } from "react";
import API_BASE_URL from "../config";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";
import { Font } from "@react-pdf/renderer";
import { Button } from "@mui/material"; // 📌 Material-UI gomb
import DownloadIcon from "@mui/icons-material/Download"; // ✅ Importáljuk az ikont
import { useTheme } from "../context/ThemeContext";
import { pdf } from "@react-pdf/renderer";

Font.register({
  family: "DejaVuSans",
  src: "/fonts/DejaVuSans.ttf",
});

Font.register({
  family: "DejaVuSans-Bold",
  src: "/fonts/DejaVuSans-Bold.ttf",
});
Font.register({
  family: "DejaVuSerif",
  fonts: [
    {
      src: "/fonts/DejaVuSerif-BoldItalic.ttf",
      fontStyle: "italic",
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#ffffff",
  },

  logo: { width: 150, alignSelf: "center", marginBottom: 10 },
  companyInfo: {
    fontSize: 8,
    textAlign: "left",
    fontFamily: "DejaVuSans-Bold",
    marginBottom: 5,
  },
  subHeader: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
    fontFamily: "DejaVuSans-Bold",
    marginBottom: 15,
  },
  table: {
    width: "100%",
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#000",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#D32F2F",
    color: "#ffffff",
    padding: 6,
    fontSize: 11,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    paddingVertical: 6,
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
    textAlign: "center",
    fontFamily: "DejaVuSans",
    paddingVertical: 2,
    paddingHorizontal: 1,
  },
  footer: {
    marginTop: 40,
    fontSize: 10,
    textAlign: "left",
    fontFamily: "DejaVuSans",
  },
  signatureBlock: {
    alignSelf: "flex-start",
    fontSize: 10,
    fontFamily: "DejaVuSans",
    marginTop: 10,
  },
  footerContainer: {
    flexDirection: "column",
    alignItems: "center",
    marginTop: 20,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 10,
  },
  contactInfo: {
    fontSize: 8,
    textAlign: "left",
    fontFamily: "DejaVuSans",
    width: "40%",
  },
  footerLogo: { width: 250, alignSelf: "center", marginBottom: 10 },
  isoCerts: { width: 150, alignSelf: "flex-end" },
  footerSection: { marginTop: "auto", textAlign: "left" },
});

const OfferDocument = ({ offerData, userData }) => {
  const mergeOfferItems = (items) => {
    const grouped = {};

    items.forEach((item) => {
      // unitType eldöntése, prioritás: BUC > ML > MP
      let unitType = "MP";
      let quantity = 0;

      if (item.quantityBUC > 0) {
        unitType = "BUC";
        quantity = item.quantityBUC;
      } else if (item.quantityML > 0) {
        unitType = "ML";
        quantity = item.quantityML;
      } else {
        unitType = "MP";
        quantity = item.quantityMP;
      }

      const description = item.customDescription?.trim() || item.numeMaterial;
      const key = `${description}__${unitType}`;

      if (!grouped[key]) {
        grouped[key] = { ...item };
        grouped[key].quantityML = unitType === "ML" ? quantity : 0;
        grouped[key].quantityMP = unitType === "MP" ? quantity : 0;
        grouped[key].quantityBUC = unitType === "BUC" ? quantity : 0;
        grouped[key].unitType = unitType;
        grouped[key].displayedQty = quantity;
      } else {
        if (unitType === "ML") {
          grouped[key].quantityML += quantity;
          grouped[key].displayedQty += quantity;
        } else if (unitType === "MP") {
          grouped[key].quantityMP += quantity;
          grouped[key].displayedQty += quantity;
        } else if (unitType === "BUC") {
          grouped[key].quantityBUC += quantity;
          grouped[key].displayedQty += quantity;
        }
      }
    });

    return Object.values(grouped);
  };

  const itemsTotal =
    offerData.items?.reduce((sum, item) => {
      let qty = 0;
      if (item.quantityBUC > 0) qty = item.quantityBUC;
      else if (item.quantityML > 0) qty = item.quantityML;
      else qty = item.quantityMP;

      return sum + qty * item.pretUnitar;
    }, 0) || 0;

  const livrare = parseFloat(offerData.pretLivrare || 0);
  const montaj =
    parseFloat(offerData.pretMontaj || 0) *
    parseFloat(offerData.cantitateMontaj || 0);

  // csak az items-re számolj kedvezményt:
  const discountAmount = itemsTotal * (offerData.discount / 100);
  const discountedSubtotal = itemsTotal - discountAmount;

  const totalNet = discountedSubtotal + livrare + montaj;
  const tva = totalNet * 0.21;
  const total = totalNet + tva;

  const formatPrice = (value) => {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };
  const formatQty = (value) => {
    return Number.isInteger(value)
      ? value.toString()
      : parseFloat(value.toFixed(2)).toString();
  };

  const formatPercent = (value) => {
    const parsed = parseFloat(value);
    return parsed % 1 === 0
      ? parsed.toString()
      : parsed.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  };

  return (
    <Document>
      <Page style={styles.page}>
        {/* Fejléc */}
        <Image style={styles.logo} src="/images/marmorexlogo.jpg" />
        <Text style={styles.companyInfo}>SC MARMOREX TRAFIC STONE SRL</Text>
        <Text style={styles.companyInfo}>CIF: RO29710766</Text>
        <Text style={styles.companyInfo}>R.C J05/190/2012</Text>
        <Text style={styles.subHeader}>OFERTĂ DE PREȚ</Text>
        <Text style={styles.subHeader}>
          {`OFERTĂ ${offerData.tipClient === 1 ? "P. J." : "P. F."} - AGENT ${
            offerData?.userName?.toUpperCase() || "AGENT"
          } - CLIENT ${offerData.customerName?.toUpperCase() || "CLIENT"}`}
        </Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableCell}>DESCRIERE</Text>
            <Text style={styles.tableCell}>Cantitate</Text>
            <Text style={styles.tableCell}>Preț unitar</Text>
            <Text style={styles.tableCell}>Cost</Text>
          </View>

          {(() => {
            const mergedItems = mergeOfferItems(offerData.items);

            return mergedItems.map((item, index) => {
              let displayedQty = 0;
              let unitLabel = "";

              if (item.quantityBUC > 0) {
                displayedQty = item.quantityBUC;
                unitLabel = "BUC";
              } else if (item.quantityML > 0) {
                displayedQty = item.quantityML;
                unitLabel = "ML";
              } else {
                displayedQty = item.quantityMP;
                unitLabel = "MP";
              }
              const cost = displayedQty * item.pretUnitar;

              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>
                    {item.customDescription?.trim() || item.numeMaterial}
                  </Text>
                  <Text style={styles.tableCell}>
                    {formatQty(displayedQty)} {unitLabel}
                  </Text>
                  <Text style={styles.tableCell}>
                    {formatPrice(item.pretUnitar)} €
                  </Text>
                  <Text style={styles.tableCell}>{formatPrice(cost)} €</Text>
                </View>
              );
            });
          })()}

          {/* Transport sor (ha van) */}
          {offerData.pretLivrare > 0 && (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>
                TRANSPORT{" "}
                {offerData.livrareRuta
                  ? offerData.livrareRuta.toUpperCase()
                  : ""}
              </Text>
              <Text style={styles.tableCell}>1 BUC</Text>
              <Text style={styles.tableCell}>
                {formatPrice(offerData.pretLivrare)} €
              </Text>

              <Text style={styles.tableCell}>
                {formatPrice(offerData.pretLivrare)} €
              </Text>
            </View>
          )}

          {/* Montaj sor*/}
          {offerData.pretMontaj > 0 && offerData.cantitateMontaj > 0 && (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>MONTAJ</Text>
              <Text style={styles.tableCell}>
                {formatQty(offerData.cantitateMontaj)} BUC
              </Text>

              <Text style={styles.tableCell}>
                {formatPrice(offerData.pretMontaj)} €
              </Text>
              <Text style={styles.tableCell}>
                {formatPrice(offerData.pretMontaj * offerData.cantitateMontaj)}{" "}
                €
              </Text>
            </View>
          )}

          {/* Összegzés */}
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}></Text>
            <Text style={[styles.tableCell, { fontWeight: "bold" }]}>
              Subtotal:
            </Text>
            <Text style={styles.tableCell}>
              {formatPrice(offerData.subtotal)} €
            </Text>
          </View>

          {offerData.discount > 0 && (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}></Text>
              <Text style={styles.tableCell}></Text>
              <Text style={[styles.tableCell, { fontWeight: "bold" }]}>
                Discount {formatPercent(offerData.discount)}%:
              </Text>

              <Text style={styles.tableCell}>
                -{formatPrice(discountAmount)} €
              </Text>
            </View>
          )}

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}></Text>
            <Text style={[styles.tableCell, { fontWeight: "bold" }]}>
              TVA 21%:
            </Text>
            <Text style={styles.tableCell}>{formatPrice(tva)} €</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}></Text>
            <Text style={[styles.tableCell, { fontWeight: "bold" }]}>
              Total:
            </Text>
            <Text style={styles.tableCell}>{formatPrice(total)} €</Text>
          </View>
        </View>

        {offerData.notita && (
          <View style={{ marginTop: 20 }}>
            <Text
              style={{
                fontSize: 10,
                fontFamily: "DejaVuSerif",
                fontStyle: "italic",
                fontWeight: 700,
                marginBottom: 5,
                textAlign: "left",
              }}
            >
              NOTĂ
            </Text>

            {offerData.notita.split("\n").map((line, idx) => (
              <Text
                key={idx} // 👉 EZ HIÁNYZOTT
                style={{
                  fontSize: 10,
                  fontFamily: "DejaVuSerif",
                  fontStyle: "italic",
                  fontWeight: 700,
                  textAlign: "left",
                  marginBottom: 2,
                }}
              >
                * {line.trim()}
              </Text>
            ))}
          </View>
        )}

        {/* Footer */}
        <View style={styles.footerSection}>
          <Text style={styles.footer}>
            {new Date(offerData.createdAt).toLocaleDateString("ro-RO")}
          </Text>
          <Text style={{ marginBottom: 10 }}></Text>
          <View style={styles.signatureBlock}>
            <Text>Cu stimă,</Text>
            <Text style={{ fontWeight: "bold" }}>{offerData.userName}</Text>
            <Text>{offerData.userPhone}</Text>
            <Text>{offerData.userEmail}</Text>
          </View>

          <View style={styles.footerContainer}>
            <Image style={styles.footerLogo} src="/images/marmorexoferta.jpg" />
          </View>

          <View style={styles.footerRow}>
            <Text style={styles.contactInfo}>
              Show room: Vladeasa, nr 32, Oradea{"\n"}
              CUI: RO29710766, J5/190/2012{"\n"}
              Tel: 0747770771
            </Text>
            <Image style={styles.isoCerts} src="/images/iso_certs.jpg" />
          </View>
        </View>
      </Page>
    </Document>
  );
};

const GenerateOfferPDF = ({ offerData, userData }) => {
  const { darkMode } = useTheme();

  if (!offerData || !userData) return null;

  const handleDownload = async () => {
    try {
      const blob = await pdf(
        <OfferDocument offerData={offerData} userData={userData} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `oferta_${offerData.nr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Eroare la generarea PDF-ului:", err);
    }
  };

  return (
    <Button
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={handleDownload}
      sx={{
        borderColor: darkMode ? "#66bb6a" : "#36993a",
        color: darkMode ? "#66bb6a" : "#36993a",
        "&:hover": {
          borderColor: darkMode ? "#66bb6a" : "#2d7d32",
          backgroundColor: darkMode
            ? "rgba(102, 187, 106, 0.1)"
            : "rgba(54, 153, 58, 0.1)",
        },
      }}
    >
      Descarcă
    </Button>
  );
};

export default GenerateOfferPDF;
